import React, { ComponentPropsWithRef, FC, forwardRef, MouseEventHandler, useCallback, useContext } from 'react';
import { Alert, Typography } from '@sravni/react-design-system';
import cn from 'classnames';
import { Col, Props, Row, Rows } from '../../../landing.common';
import commonStyles from './common.module.scss';
import { StepContext } from '../../creator/step.context';
import { useIsMobile } from '@sravni/react-utils';

const { Text } = Typography;

export const StepContainer: FC<React.ComponentProps<'div'> & Props> = ({ children, className, ...other }) => {
    return (
        <Rows className={className} {...other}>
            {children}
        </Rows>
    );
};

export const StepSubmitBlock: FC<React.ComponentProps<'div'>> = ({ children, className, ...other }) => {
    return (
        <Rows gap="16" className={cn(commonStyles['submit-block'], className)} {...other}>
            <Row gap="16" mobileGap="12">
                <Col>{children}</Col>
            </Row>
        </Rows>
    );
};

export const StepSubtitle: FC<React.ComponentPropsWithoutRef<'span'>> = ({ children, className, ...other }) => {
    return (
        <Row>
            <Col>
                <Text size={16} className={cn(commonStyles['step-subtitle'], className)} {...other}>
                    {children}
                </Text>
            </Col>
        </Row>
    );
};

export const FocusFieldWrapper: FC<{ name: string; field: JSX.Element; disabled?: boolean }> = ({
    name,
    field,
    disabled,
}) => {
    const isMobile = useIsMobile();
    const { focusedField, setFocusedField } = useContext(StepContext);
    const isShowField = useCallback(
        (fieldName: string) => {
            return !isMobile || fieldName !== focusedField;
        },
        [isMobile, focusedField],
    );

    const onClickHandler: MouseEventHandler<HTMLDivElement> = useCallback(
        (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (focusedField !== name) {
                setFocusedField(name);
            }
        },
        [setFocusedField, name],
    );

    if (!isShowField(name)) {
        return null;
    }

    return (
        <div className={commonStyles['field-wrapper']} id={`field_${name}`}>
            <div
                className={commonStyles['field-wrapper_click-handler']}
                onClick={isMobile && !disabled ? onClickHandler : undefined}
            />
            {field}
        </div>
    );
};

type FocusedFieldWithAlertProps = ComponentPropsWithRef<'div'> & {
    alert?: string;
    field: JSX.Element;
    showAlert?: boolean;
};

export const FocusedFieldWithAlert: FC<FocusedFieldWithAlertProps> = forwardRef(function FocusedFieldWithAlertComponent(
    { alert, field, showAlert },
    ref,
) {
    if (!showAlert) {
        return <>{field}</>;
    }

    return (
        <div className={commonStyles['field-with-alert']} ref={ref}>
            {field}
            <Alert variant="secondary" color="orange" className={commonStyles['field-with-alert__alert']}>
                {alert}
            </Alert>
        </div>
    );
});
